import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, FileText, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import DashboardLayout from "../../components/DashboardLayout";
import TrialBlocker from "../../components/TrialBlocker";
import { TravelPromoBanner } from "../../components/Client/PlanSection";
import { FloatingTips, TipsBanner } from "../../components/Client/ContextualTips";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DS160Step = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [formData, setFormData] = useState({
    confirmation_number: "",
    completed: false
  });

  useEffect(() => {
    fetchCase();
  }, []);

  const fetchCase = async () => {
    try {
      const res = await axios.get(`${API}/cases/my-case`);
      setCaseData(res.data);
      if (res.data?.ds160_data) {
        setFormData(res.data.ds160_data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!caseData?.id) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/cases/${caseData.id}/ds160`, formData);
      toast.success(formData.completed ? "DS-160 marcado como completado" : "Información guardada");
      if (formData.completed) {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <TrialBlocker feature="Formulario DS-160">
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Preparación DS-160
          </h1>
          <p className="text-navy-600">
            El formulario DS-160 es la solicitud oficial de visa de no inmigrante.
          </p>
        </motion.div>

        {/* Tips Banner */}
        <TipsBanner context="ds160" subContext="general" />

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`card-elevated p-6 mb-8 border-l-4 ${
            formData.completed ? "border-emerald-500" : "border-amber-500"
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              formData.completed ? "bg-emerald-100" : "bg-amber-100"
            }`}>
              {formData.completed ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              ) : (
                <Clock className="w-6 h-6 text-amber-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-navy-900">
                {formData.completed ? "DS-160 Completado" : "DS-160 En Proceso"}
              </h3>
              <p className="text-sm text-navy-500">
                {formData.completed 
                  ? "Tu formulario ha sido procesado exitosamente" 
                  : "Nuestro equipo está preparando tu formulario"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-elevated p-6 mb-8"
        >
          <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
            ¿Qué es el DS-160?
          </h3>
          <div className="space-y-4 text-navy-600">
            <p>
              El DS-160 es el formulario de solicitud de visa de no inmigrante que debe ser 
              completado por todos los solicitantes de visa B1/B2.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">Proceso con Divídelo</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Nuestro equipo legal completará el DS-160 por ti usando la información 
                    que proporcionaste. Te notificaremos cuando esté listo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Confirmation Number */}
        {formData.completed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-elevated p-6 mb-8"
          >
            <div className="space-y-4">
              <Label className="text-navy-700 font-medium">Número de Confirmación DS-160</Label>
              <div className="flex items-center gap-3">
                <div className="flex-1 p-4 bg-navy-50 rounded-lg border border-navy-200 font-mono text-lg">
                  {formData.confirmation_number || "Pendiente"}
                </div>
              </div>
              <p className="text-sm text-navy-500">
                Guarda este número, lo necesitarás para tu cita en el CAS.
              </p>
            </div>
          </motion.div>
        )}

        {/* Checklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-elevated p-6"
        >
          <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
            Checklist DS-160
          </h3>
          <div className="space-y-3">
            {[
              "Información personal verificada",
              "Historial de viajes actualizado",
              "Información laboral confirmada",
              "Datos de contacto en USA (si aplica)",
              "Fotografía digital cargada"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-navy-700">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex justify-end"
        >
          <Button
            onClick={() => navigate("/dashboard")}
            className="bg-gold-600 hover:bg-gold-700 text-white"
          >
            Volver al Dashboard
          </Button>
        </motion.div>

        {/* Travel Promo Banner - Strategic placement on DS-160 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <div className="mb-3">
            <p className="text-sm text-navy-600 font-medium">
              💡 ¿Sabías que tener una reservación de viaje fortalece tu solicitud DS-160?
            </p>
          </div>
          <TravelPromoBanner vendedorInfo={user?.vendedor_info} userRole={user?.role} />
        </motion.div>

        {/* Floating Tips */}
        <FloatingTips context="ds160" subContext="personal" />
      </div>
    </DashboardLayout>
    </TrialBlocker>
  );
};

export default DS160Step;
